import { ColumnDef } from '@tanstack/react-table'
import { LazyAdCampaign } from 'app/components/contacts/LazyAdCampaign'
import { DateComponent } from 'app/components/DateComponent'
import {
  generalContactColumns,
  marketingReferralColumns,
  contactDetailColumns,
  contactIdentityColumns,
  nameColumn,
  locationColumn,
  tagsColumn,
  channelColumn,
  utmEditableColumns,
  adPlatformColumn,
  reportedAttributionColumn,
  marketingUtmColumnsWithPermissions,
  scheduledProductionColumn,
  productionLtvColumn,
  collectedLtvColumn,
  appointmentReferralColumns,
  firstVisitValueColumn,
  type MarketingColumnsFactory,
} from 'app/table-columns/sharedContactColumns'
import type { ContactJson } from 'app/types/entities'
import { getLeadStatusColorMap } from 'lib/colorMappings'

export const defaultColumns: MarketingColumnsFactory = (
  contactHandler,
  updateContact,
  closeDrawerHandler,
  checkPermission,
  isSMC,
  isSuper,
) => {
  const marketingColumns: ColumnDef<ContactJson, any>[] = [
    adPlatformColumn,
    {
      id: `Ad Campaign`,
      header: `Ad Campaign`,
      enableSorting: false,
      cell: (props) => <LazyAdCampaign campaignId={props.getValue()} />,
      accessorKey: `trackableCampaignId`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
      },
    },
  ]
  const utmColumns: ColumnDef<ContactJson, any>[] = [
    ...utmEditableColumns({ isSMC, updateContact, closeDrawerHandler, defaultVisible: false }),
    reportedAttributionColumn,
  ]
  const columnsWithPermissions = marketingUtmColumnsWithPermissions({
    isSuper,
    isSMC,
    checkPermission,
    marketingColumns,
    utmColumns,
  })
  return [
    nameColumn(contactHandler),
    ...contactIdentityColumns,
    locationColumn(),
    tagsColumn<ContactJson>(),
    ...contactDetailColumns,
    {
      id: `Link Status`,
      accessorKey: `linkStatus`,
      header: `Link Status`,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `linkStatus.keyword`,
      },
    },
    {
      id: `firstAppointmentDate`,
      accessorKey: `firstAppointmentDate`,
      header: `First Appointment Date`,
      cell: (props) => <DateComponent value={props.row.original.firstAppointmentDate} dateOnly />,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `firstAppointmentDate`,
      },
    },
    {
      id: `lastAppointmentDate`,
      accessorKey: `lastAppointmentDate`,
      header: `Last Appointment Date`,
      cell: (props) => <DateComponent value={props.row.original.lastAppointmentDate} dateOnly />,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `lastAppointmentDate`,
      },
    },
    {
      id: `Nearest Appointment Date`,
      accessorKey: `nearestAppointmentDate`,
      header: `Nearest Appointment Date`,
      cell: (props) => <DateComponent value={props.row.original.nearestAppointmentDate} dateOnly />,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `nearestAppointmentDate`,
      },
    },
    ...appointmentReferralColumns,
    collectedLtvColumn,
    productionLtvColumn,
    firstVisitValueColumn,
    scheduledProductionColumn,
    {
      id: `leadStatus`,
      accessorKey: `leadStatus`,
      header: `Lead Status`,
      cell: (props) => (
        <>
          <span
            className="mr-1.5 inline-block h-2 w-2 rounded-[2px] align-middle"
            style={{ backgroundColor: getLeadStatusColorMap(props.row.original.leadStatus) }}
          ></span>
          <span>{props.row.original.leadStatus}</span>
        </>
      ),
      meta: {
        group: `Marketing`,
        defaultVisible: true,
        sortKey: `leadStatus.keyword`,
      },
    },
    ...(isSuper
      ? [
          {
            id: `neededAction`,
            accessorFn: (row) => (row.neededAction ? `Yes` : `No`),
            header: `Needed Action`,
            meta: {
              group: `Marketing`,
              defaultVisible: true,
              sortKey: `neededAction`,
            },
          },
        ]
      : []),
    {
      id: `closedLostReason`,
      accessorKey: `closedLostReason`,
      header: `Closed Lost Reason`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `closedLostReason.keyword`,
      },
    },
    channelColumn,
    ...marketingReferralColumns,
    {
      id: `lifecycleStage`,
      accessorKey: `lifecycleStage`,
      header: `Lifecycle Stage`,
      meta: {
        group: `General`,
        defaultVisible: true,
        pinned: false,
        sortKey: `lifecycleStage.keyword`,
      },
    },
    ...generalContactColumns,
    {
      id: `firstActivityDate`,
      accessorKey: `firstActivityDate`,
      header: `First Activity Date`,
      cell: (props) => <DateComponent value={props.row.original.firstActivityDate} dateOnly />,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `firstActivityDate`,
      },
    },
    {
      id: `lastActivityDate`,
      accessorKey: `lastActivityDate`,
      header: `Last Activity Date`,
      cell: (props) => <DateComponent value={props.row.original.lastActivityDate} dateOnly />,
      meta: {
        group: `General`,
        defaultVisible: false,
        pinned: false,
        sortKey: `lastActivityDate`,
      },
    },
    {
      id: `preferredContact`,
      accessorKey: `preferredContact`,
      header: `Preferred Contact`,
      meta: {
        sortKey: `preferredContact.keyword`,
        group: `Communication`,
        defaultVisible: false,
        pinned: false,
      },
    },
    {
      id: `preferredCommunicationMethod`,
      accessorKey: `preferredCommunicationMethod`,
      header: `Preferred Communication Method`,
      meta: {
        group: `Communication`,
        defaultVisible: false,
        pinned: false,
        sortKey: `preferredCommunicationMethod.keyword`,
      },
    },
    {
      id: `becameLeadDate`,
      accessorKey: `becameLeadDate`,
      header: `Became a Lead Date`,
      cell: (props) => DateComponent({ value: props.row.original.becameLeadDate, dateOnly: true }),
      meta: {
        group: `Marketing`,
        defaultVisible: true,
        sortKey: `becameLeadDate`,
      },
    },
    {
      id: `firstAppointmentMadeDate`,
      accessorKey: `firstAppointmentMadeDate`,
      header: `Became a New Patient Scheduled Date`,
      cell: (props) => DateComponent({ value: props.row.original.firstAppointmentMadeDate, dateOnly: true }),
      meta: {
        group: `Marketing`,
        defaultVisible: true,
        sortKey: `firstAppointmentMadeDate`,
      },
    },
    {
      id: `becamePatientDate`,
      accessorKey: `becamePatientDate`,
      header: `Became a Patient Date`,
      cell: (props) => DateComponent({ value: props.row.original.becamePatientDate, dateOnly: true }),
      meta: {
        group: `PMS`,
        defaultVisible: true,
        sortKey: `becamePatientDate`,
      },
    },
    {
      id: `patientCreatedDate`,
      accessorKey: `patientCreatedDate`,
      header: `Patient Created Date`,
      cell: (props) => DateComponent({ value: props.row.original.patientCreatedDate, dateOnly: true }),
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `patientCreatedDate`,
      },
    },
    {
      id: `becameInactiveDate`,
      header: `Became Inactive Date`,
      cell: (props) => DateComponent({ value: props.row.original.becameInactiveDate, dateOnly: true }),
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `becameInactiveDate`,
      },
    },
    {
      id: `firstActivityType`,
      header: `First Activity Type`,
      accessorKey: `firstActivityType`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `firstActivityType.keyword`,
      },
    },
    {
      id: `First Transaction Date`,
      accessorKey: `firstTransactionDate`,
      header: `First Transaction Date`,
      cell: (props) => DateComponent({ value: props.row.original.firstTransactionDate, dateOnly: true }),
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `firstTransactionDate`,
      },
    },
    {
      id: `Last Transaction Date`,
      accessorKey: `lastTransactionDate`,
      header: `Last Transaction Date`,
      cell: (props) => DateComponent({ value: props.row.original.lastTransactionDate, dateOnly: true }),
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `lastTransactionDate`,
      },
    },
    {
      id: `Ad Click Date`,
      accessorKey: `adClickDate`,
      header: `Ad Click Date`,
      cell: (props) => DateComponent({ value: props.row.original.adClickDate, dateOnly: true }),
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `adClickDate`,
      },
    },
    {
      id: `Needs Review`,
      accessorKey: `needsReview`,
      header: `Needs Review`,
      meta: {
        group: `General`,
        defaultVisible: false,
        sortKey: `needsReview`,
      },
    },
    {
      id: `Recare Count`,
      accessorKey: `recareCount`,
      header: `Recare Count`,
      meta: {
        group: `PMS`,
        defaultVisible: false,
        sortKey: `recareCount`,
      },
    },
    {
      id: `schedulingActivityType`,
      header: `Scheduling Activity Type`,
      accessorKey: `schedulingActivityType`,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
        sortKey: `schedulingActivityType.keyword`,
      },
    },
    {
      id: `Has Activity`,
      header: `Has Activity`,
      accessorFn: (props) => props.activityTypes,
      cell: (props) => <>{props.getValue()?.join(`, `)}</>,
      meta: {
        group: `Marketing`,
        defaultVisible: false,
      },
      enableSorting: false,
    },
    ...columnsWithPermissions,
  ]
}
